import { useEffect } from 'react';
import { useUnlockDialogContext } from './UnlockDialog.context';

type AutoUnlockGateProps = Readonly<{
  hasPages: boolean;
  hasUnlocked: boolean;
  isPreviewing: boolean;
  // True when any full-page gate (WelcomePagePasswordGate or WelcomePageOtpGate)
  // is rendering. In that case the gate owns the unlock CTA and auto-opening
  // the legacy dialog on top would conflict.
  isFullPageGateActive: boolean;
  // Draft-preview grant: never auto-open the unlock dialog — see MCP_PREVIEW.md#visibility-only.
  canPreviewDraft: boolean;
}>;

/**
 * Auto-opens the unlock dialog when a guest visits an event where all pages
 * are password-protected (or hidden) and the guest hasn't unlocked yet.
 *
 * Without this, the guest sees a blank page with no way to enter the password,
 * because the server filters out all passwordProtected pages when hasUnlocked is false.
 *
 * Re-opens the dialog if the guest dismisses it while still locked out,
 * since there's no content to show behind it.
 *
 * Suppressed when a full-page gate is active — either the wedding password
 * gate (`WelcomePagePasswordGate`) or the general-event identification gate
 * (`WelcomePageOtpGate`). The gate component owns the unlock CTA in that case.
 */
export const AutoUnlockGate: React.FC<AutoUnlockGateProps> = ({ hasPages, hasUnlocked, isPreviewing, isFullPageGateActive, canPreviewDraft }) => {
  const { isOpen, handleUnlockDialogOpen } = useUnlockDialogContext();

  const shouldAutoOpen = !hasPages && !hasUnlocked && !isPreviewing && !isFullPageGateActive && !canPreviewDraft;

  useEffect(() => {
    if (shouldAutoOpen && !isOpen) {
      handleUnlockDialogOpen();
    }
  }, [shouldAutoOpen, isOpen, handleUnlockDialogOpen]);

  return null;
};
